import { useState, useMemo } from 'react';
import { Link } from 'react-router';
import {
  Shield, FileText, AlertTriangle, BarChart3, Zap, Clock, CheckCircle,
  TrendingUp, Users, Target, User, Bot, ArrowRight,
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell,
} from 'recharts';
import { useSimulaData } from '../hooks/useSimulaData';
import { useAuth } from '../context/AuthContext';
import { isConcludedAuditStatus } from '../utils/auditStatus';

export function QualityDashboard() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'superadmin' || user?.role === 'admin';
  const {
    monitors: monEntity, audits: audEntity, operators: opEntity,
    sectors: secEntity, supervisors: supEntity,
    monitorGoals: mgEntity, monitorPerformance: mpEntity, auditQueue: aqEntity,
  } = useSimulaData();
  const monitors = monEntity.data;
  const audits = audEntity.data;
  const operators = opEntity.data;
  const sectors = secEntity.data;
  const supervisors = supEntity.data;
  const monitorGoals = mgEntity.data;
  const monitorPerformance = mpEntity.data;
  const auditQueue = aqEntity.data;

  // Se o usuario de qualidade tiver monitorId vinculado, filtrar por ele; senão, admin vê tudo
  const [selectedMonitorId, setSelectedMonitorId] = useState<string>(
    user?.monitorId ? user.monitorId : 'all'
  );

  const humanMonitors = monitors.filter(m => m.type === 'human');
  const allMonitors = monitors;

  const effectiveMonitorId = user?.monitorId && !isAdmin ? user.monitorId : selectedMonitorId;
  const currentMonitor = monitors.find(m => m.id === effectiveMonitorId);

  // Filtrar dados por monitor
  const filteredAudits = effectiveMonitorId === 'all'
    ? audits.filter(a => isConcludedAuditStatus(a.status))
    : audits.filter(a => a.monitorId === effectiveMonitorId && isConcludedAuditStatus(a.status));

  const filteredQueue = effectiveMonitorId === 'all'
    ? auditQueue
    : auditQueue.filter(q => q.assignedMonitorId === effectiveMonitorId);

  const filteredGoals = effectiveMonitorId === 'all'
    ? monitorGoals
    : monitorGoals.filter(g => g.monitorId === effectiveMonitorId);

  // Período atual dinâmico (YYYY-MM)
  const currentPeriod = useMemo(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  }, []);

  const filteredPerf = effectiveMonitorId === 'all'
    ? monitorPerformance.filter(p => p.period === currentPeriod)
    : monitorPerformance.filter(p => p.monitorId === effectiveMonitorId && p.period === currentPeriod);

  const currentPerf = filteredPerf[0];

  // KPIs calculados
  const evaluatedAudits = filteredAudits.filter(a => a.evaluation);
  const totalAudits = evaluatedAudits.length;
  const avgScore = totalAudits > 0
    ? Math.round(evaluatedAudits.reduce((sum, a) => sum + (a.evaluation?.score || 0), 0) / totalAudits)
    : 0;
  const fgCount = evaluatedAudits.filter(a => a.evaluation?.hasFalhaGrave).length;
  const fgRate = totalAudits > 0 ? Math.round((fgCount / totalAudits) * 100) : 0;
  const pendingCount = filteredQueue.length;
  const slaRate = currentPerf
    ? Math.round((currentPerf.completedOnTime / Math.max(currentPerf.totalAssigned, 1)) * 100)
    : 0;

  // Dados para grafico de barras - auditorias por monitor
  const auditsByMonitor = allMonitors.map(m => {
    const mAudits = audits.filter(a => a.monitorId === m.id && isConcludedAuditStatus(a.status) && a.evaluation);
    return {
      name: m.name.length > 14 ? m.name.slice(0, 14) + '...' : m.name,
      auditorias: mAudits.length,
      type: m.type,
    };
  });

  // Dados para pizza - distribuicao human vs AI
  const humanAudits = audits.filter(a => a.monitorType === 'human' && isConcludedAuditStatus(a.status) && a.evaluation).length;
  const aiAudits = audits.filter(a => a.monitorType === 'ai' && isConcludedAuditStatus(a.status) && a.evaluation).length;
  const pieData = [
    { name: 'Humana', value: humanAudits },
    { name: 'IA', value: aiAudits },
  ];
  const pieColors = ['var(--primary)', 'var(--secondary)'];

  // Dados para line chart - tendencia mensal (derivado de monitorPerformance)
  const trendData = useMemo(() => {
    const monthNames = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
    // Obter todos os períodos disponíveis
    const relevantPerf = effectiveMonitorId === 'all'
      ? monitorPerformance
      : monitorPerformance.filter(p => p.monitorId === effectiveMonitorId);

    const byPeriod: Record<string, { audits: number; scores: number[]; fg: number }> = {};
    relevantPerf.forEach(p => {
      if (!byPeriod[p.period]) byPeriod[p.period] = { audits: 0, scores: [], fg: 0 };
      byPeriod[p.period].audits += p.auditsPerformed;
      byPeriod[p.period].scores.push(p.avgScoreGiven);
      byPeriod[p.period].fg += p.falhasGravesDetected;
    });

    return Object.entries(byPeriod)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([period, data]) => {
        const [, monthStr] = period.split('-');
        const monthIdx = parseInt(monthStr, 10) - 1;
        return {
          mes: monthNames[monthIdx] || period,
          auditorias: data.audits,
          score: data.scores.length > 0 ? Math.round(data.scores.reduce((s, v) => s + v, 0) / data.scores.length) : 0,
          fg: data.fg,
        };
      });
  }, [monitorPerformance, effectiveMonitorId]);

  // Prioridade da fila
  const highPriority = filteredQueue.filter(q => q.priority === 'high').length;
  const overdueSla = filteredQueue.filter(q => new Date(q.slaDeadline) < new Date()).length;

  const cardStyle = {
    backgroundColor: 'var(--card)',
    borderRadius: 'var(--radius-card)',
    border: '1px solid var(--border)',
  };

  const labelStyle = {
    fontSize: 'var(--text-xs)',
    color: 'var(--muted-foreground)',
    fontFamily: 'var(--font-family-inter)',
  };

  const valueStyle = (color: string) => ({
    fontSize: 'var(--text-2xl)',
    fontWeight: 'var(--font-weight-semibold)' as const,
    color,
    fontFamily: 'var(--font-family-inter)',
  });

  const getCategoryLabel = (cat: string) => {
    switch (cat) {
      case 'audits_quota': return 'Cota de Auditorias';
      case 'avg_score_target': return 'Score Medio';
      case 'fg_detection': return 'Deteccao FG';
      case 'calibration': return 'Calibracao';
      case 'sla': return 'SLA';
      default: return cat;
    }
  };

  const getCategoryColor = (cat: string) => {
    switch (cat) {
      case 'audits_quota': return 'var(--primary)';
      case 'avg_score_target': return 'var(--secondary)';
      case 'fg_detection': return 'var(--error)';
      case 'calibration': return 'var(--warning)';
      case 'sla': return 'var(--chart-4)';
      default: return 'var(--foreground)';
    }
  };

  return (
    <div style={{ fontFamily: 'var(--font-family-inter)' }}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <Shield size={28} style={{ color: 'var(--primary)' }} />
            <h1 style={{ fontSize: 'var(--text-2xl)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--foreground)' }}>
              Dashboard de Qualidade
            </h1>
          </div>
          <p style={{ color: 'var(--muted-foreground)', fontSize: 'var(--text-sm)' }}>
            {currentMonitor
              ? `Monitor: ${currentMonitor.name} (${currentMonitor.type === 'ai' ? 'IA' : 'Humano'}) — ${currentMonitor.specialization}`
              : 'Visao geral de todos os monitores'}
          </p>
        </div>

        <div className="flex items-center gap-3">
          {/* Seletor de monitor (admin/qualidade) */}
          {isAdmin && (
            <select
              value={selectedMonitorId}
              onChange={(e) => setSelectedMonitorId(e.target.value)}
              style={{
                padding: '8px 12px',
                borderRadius: 'var(--radius-input)',
                border: '1px solid var(--border)',
                backgroundColor: 'var(--input-background)',
                color: 'var(--foreground)',
                fontSize: 'var(--text-sm)',
                fontFamily: 'var(--font-family-inter)',
                cursor: 'pointer',
              }}
            >
              <option value="all">Todos os Monitores</option>
              {allMonitors.map(m => (
                <option key={m.id} value={m.id}>
                  {m.name} ({m.type === 'ai' ? 'IA' : 'Humano'})
                </option>
              ))}
            </select>
          )}

          <Link
            to="/qualidade/fila-auditorias"
            className="flex items-center gap-2 px-4 py-2"
            style={{
              backgroundColor: 'var(--primary)',
              color: 'var(--primary-foreground)',
              borderRadius: 'var(--radius-button)',
              textDecoration: 'none',
              fontSize: 'var(--text-sm)',
              fontWeight: 'var(--font-weight-medium)',
            }}
          >
            <FileText size={16} />
            Fila de Auditorias
          </Link>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
        {[
          { label: 'Auditorias Realizadas', value: totalAudits, icon: FileText, color: 'var(--primary)', testId: 'quality-kpi-total-audits' },
          { label: 'Score Medio', value: avgScore, icon: BarChart3, color: avgScore >= 70 ? 'var(--success)' : 'var(--warning)', testId: 'quality-kpi-avg-score' },
          { label: 'Falhas Graves', value: fgCount, icon: AlertTriangle, color: fgCount > 0 ? 'var(--error)' : 'var(--success)', testId: 'quality-kpi-fg-count' },
          { label: 'Taxa FG', value: `${fgRate}%`, icon: Zap, color: fgRate > 20 ? 'var(--error)' : 'var(--warning)', testId: 'quality-kpi-fg-rate' },
          { label: 'Pendentes na Fila', value: pendingCount, icon: Clock, color: pendingCount > 5 ? 'var(--warning)' : 'var(--secondary)', testId: 'quality-kpi-pending-queue' },
          { label: 'SLA Cumprido', value: `${slaRate}%`, icon: CheckCircle, color: slaRate >= 90 ? 'var(--success)' : 'var(--warning)', testId: 'quality-kpi-sla-rate' },
        ].map((kpi) => (
          <div key={kpi.label} data-testid={kpi.testId} className="p-4" style={cardStyle}>
            <div className="flex items-center justify-between mb-2">
              <p style={labelStyle}>{kpi.label}</p>
              <kpi.icon size={16} style={{ color: kpi.color }} />
            </div>
            <p data-testid={`${kpi.testId}-value`} style={valueStyle(kpi.color)}>{kpi.value}</p>
          </div>
        ))}
      </div>

      {/* Alertas de fila */}
      {(highPriority > 0 || overdueSla > 0) && (
        <div
          className="flex items-center gap-4 p-4 mb-6 flex-wrap"
          style={{
            ...cardStyle,
            borderColor: 'var(--warning)',
            backgroundColor: 'var(--destructive-muted)',
          }}
        >
          <AlertTriangle size={20} style={{ color: 'var(--warning)' }} />
          <div className="flex-1">
            <p style={{ fontWeight: 'var(--font-weight-semibold)', fontSize: 'var(--text-sm)', color: 'var(--foreground)' }}>
              Atencao: Fila de Auditorias
            </p>
            <p style={{ fontSize: 'var(--text-xs)', color: 'var(--muted-foreground)' }}>
              {highPriority > 0 && `${highPriority} auditoria(s) de alta prioridade. `}
              {overdueSla > 0 && `${overdueSla} auditoria(s) com SLA vencido.`}
            </p>
          </div>
          <Link
            to="/qualidade/fila-auditorias"
            className="flex items-center gap-1 px-3 py-1.5"
            style={{
              backgroundColor: 'var(--warning)',
              color: 'var(--warning-foreground)',
              borderRadius: 'var(--radius-button)',
              textDecoration: 'none',
              fontSize: 'var(--text-xs)',
              fontWeight: 'var(--font-weight-semibold)',
            }}
          >
            Ver Fila <ArrowRight size={14} />
          </Link>
        </div>
      )}

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        {/* Auditorias por Monitor */}
        <div className="p-5 lg:col-span-2" style={cardStyle}>
          <h3 style={{ fontWeight: 'var(--font-weight-semibold)', fontSize: 'var(--text-base)', marginBottom: '16px', color: 'var(--foreground)' }}>
            Auditorias por Monitor
          </h3>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={auditsByMonitor}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
              <XAxis dataKey="name" tick={{ fill: 'var(--muted-foreground)', fontSize: 11 }} />
              <YAxis tick={{ fill: 'var(--muted-foreground)', fontSize: 11 }} />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'var(--card)',
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius)',
                  fontSize: 'var(--text-sm)',
                }}
              />
              <Bar dataKey="auditorias" fill="var(--primary)" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Distribuicao Humana vs IA */}
        <div className="p-5" style={cardStyle}>
          <h3 style={{ fontWeight: 'var(--font-weight-semibold)', fontSize: 'var(--text-base)', marginBottom: '16px', color: 'var(--foreground)' }}>
            Humana vs IA
          </h3>
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie
                data={pieData}
                cx="50%"
                cy="50%"
                innerRadius={50}
                outerRadius={80}
                dataKey="value"
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
              >
                {pieData.map((_, i) => (
                  <Cell key={i} fill={pieColors[i]} />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{
                  backgroundColor: 'var(--card)',
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius)',
                }}
              />
            </PieChart>
          </ResponsiveContainer>
          <div className="flex justify-center gap-6 mt-2">
            <div className="flex items-center gap-2">
              <User size={14} style={{ color: 'var(--primary)' }} />
              <span style={{ fontSize: 'var(--text-xs)', color: 'var(--muted-foreground)' }}>Humana ({humanAudits})</span>
            </div>
            <div className="flex items-center gap-2">
              <Bot size={14} style={{ color: 'var(--secondary)' }} />
              <span style={{ fontSize: 'var(--text-xs)', color: 'var(--muted-foreground)' }}>IA ({aiAudits})</span>
            </div>
          </div>
        </div>
      </div>

      {/* Metas + Performance */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Metas do Monitor */}
        <div className="p-5" style={cardStyle}>
          <div className="flex items-center gap-2 mb-4">
            <Target size={20} style={{ color: 'var(--primary)' }} />
            <h3 style={{ fontWeight: 'var(--font-weight-semibold)', fontSize: 'var(--text-base)', color: 'var(--foreground)' }}>
              Metas do Monitor
            </h3>
          </div>
          {filteredGoals.length === 0 ? (
            <p style={{ color: 'var(--muted-foreground)', fontSize: 'var(--text-sm)', textAlign: 'center', padding: '24px 0' }}>
              Nenhuma meta encontrada para este monitor.
            </p>
          ) : (
            <div className="space-y-3">
              {filteredGoals.map((goal) => {
                const progress = Math.min(Math.round((goal.current / Math.max(goal.target, 1)) * 100), 100);
                const isOverdue = new Date(goal.deadline) < new Date() && !goal.isCompleted;
                const categoryColor = getCategoryColor(goal.category);
                return (
                  <div
                    key={goal.id}
                    className="p-3"
                    style={{
                      backgroundColor: goal.isCompleted
                        ? 'color-mix(in srgb, var(--success) 5%, transparent)'
                        : isOverdue
                          ? 'var(--error-muted)'
                          : 'var(--muted)',
                      borderRadius: 'var(--radius)',
                      borderLeft: `3px solid ${goal.isCompleted ? 'var(--success)' : isOverdue ? 'var(--error)' : categoryColor}`,
                    }}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span style={{ fontWeight: 'var(--font-weight-medium)', fontSize: 'var(--text-sm)', color: 'var(--foreground)' }}>
                        {goal.title}
                      </span>
                      <span
                        className="px-2 py-0.5"
                        style={{
                          fontSize: 'var(--text-micro)',
                          borderRadius: 'var(--radius)',
                          backgroundColor: `color-mix(in srgb, ${categoryColor} 12%, transparent)`,
                          color: categoryColor,
                          fontWeight: 'var(--font-weight-semibold)',
                        }}
                      >
                        {getCategoryLabel(goal.category)}
                      </span>
                    </div>
                    <p style={{ fontSize: 'var(--text-xs)', color: 'var(--muted-foreground)', marginBottom: '8px' }}>
                      {goal.description}
                    </p>
                    <div className="flex items-center gap-3">
                      <div
                        className="flex-1"
                        style={{
                          height: '6px',
                          backgroundColor: 'var(--border)',
                          borderRadius: '3px',
                          overflow: 'hidden',
                        }}
                      >
                        <div
                          style={{
                            width: `${progress}%`,
                            height: '100%',
                            backgroundColor: goal.isCompleted ? 'var(--success)' : categoryColor,
                            borderRadius: '3px',
                            transition: 'width 0.3s',
                          }}
                        />
                      </div>
                      <span style={{ fontSize: 'var(--text-xs)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--foreground)', fontFamily: 'var(--font-family-mono)' }}>
                        {goal.current}/{goal.target}
                      </span>
                      {goal.isCompleted && <CheckCircle size={14} style={{ color: 'var(--success)' }} />}
                    </div>
                    <p style={{ fontSize: 'var(--text-micro)', color: isOverdue ? 'var(--error)' : 'var(--muted-foreground)', marginTop: '4px' }}>
                      Prazo: {new Date(goal.deadline).toLocaleDateString('pt-BR')}
                      {isOverdue && ' — VENCIDO'}
                    </p>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Performance detalhada */}
        <div className="p-5" style={cardStyle}>
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp size={20} style={{ color: 'var(--secondary)' }} />
            <h3 style={{ fontWeight: 'var(--font-weight-semibold)', fontSize: 'var(--text-base)', color: 'var(--foreground)' }}>
              Performance Detalhada
            </h3>
          </div>
          {currentPerf ? (
            <div className="space-y-3">
              {[
                { label: 'Auditorias Realizadas', value: currentPerf.auditsPerformed, icon: FileText, color: 'var(--primary)' },
                { label: 'Score Medio Atribuido', value: currentPerf.avgScoreGiven, icon: BarChart3, color: currentPerf.avgScoreGiven >= 70 ? 'var(--success)' : 'var(--warning)' },
                { label: 'Falhas Graves Detectadas', value: currentPerf.falhasGravesDetected, icon: AlertTriangle, color: 'var(--error)' },
                { label: 'Tempo Medio/Auditoria', value: `${currentPerf.avgAuditDuration} min`, icon: Clock, color: 'var(--secondary)' },
                { label: 'Score de Calibracao', value: `${currentPerf.calibrationScore}%`, icon: Target, color: currentPerf.calibrationScore >= 85 ? 'var(--success)' : 'var(--warning)' },
                { label: 'Pendentes', value: currentPerf.pendingAudits, icon: Clock, color: currentPerf.pendingAudits > 5 ? 'var(--warning)' : 'var(--success)' },
                { label: 'Concluidas no Prazo', value: `${currentPerf.completedOnTime}/${currentPerf.totalAssigned}`, icon: CheckCircle, color: 'var(--success)' },
              ].map((item) => (
                <div
                  key={item.label}
                  className="flex items-center justify-between p-3"
                  style={{
                    backgroundColor: 'var(--muted)',
                    borderRadius: 'var(--radius)',
                  }}
                >
                  <div className="flex items-center gap-2">
                    <item.icon size={16} style={{ color: item.color }} />
                    <span style={{ fontSize: 'var(--text-sm)', color: 'var(--foreground)' }}>{item.label}</span>
                  </div>
                  <span style={{ fontWeight: 'var(--font-weight-semibold)', fontSize: 'var(--text-sm)', color: item.color, fontFamily: 'var(--font-family-mono)' }}>
                    {item.value}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p style={{ color: 'var(--muted-foreground)', fontSize: 'var(--text-sm)', textAlign: 'center', padding: '24px 0' }}>
              Selecione um monitor para ver a performance detalhada.
            </p>
          )}
        </div>
      </div>

      {/* Fila de auditorias (preview) */}
      <div className="p-5" style={cardStyle}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Clock size={20} style={{ color: 'var(--warning)' }} />
            <h3 style={{ fontWeight: 'var(--font-weight-semibold)', fontSize: 'var(--text-base)', color: 'var(--foreground)' }}>
              Proximas Auditorias na Fila
            </h3>
            <span
              className="px-2 py-0.5"
              style={{
                fontSize: 'var(--text-xs)',
                backgroundColor: 'var(--primary)',
                color: 'var(--primary-foreground)',
                borderRadius: 'var(--radius)',
                fontWeight: 'var(--font-weight-semibold)',
              }}
            >
              {filteredQueue.length}
            </span>
          </div>
          <Link
            to="/qualidade/fila-auditorias"
            className="flex items-center gap-1"
            style={{
              color: 'var(--primary)',
              textDecoration: 'none',
              fontSize: 'var(--text-sm)',
              fontWeight: 'var(--font-weight-medium)',
            }}
          >
            Ver Todas <ArrowRight size={14} />
          </Link>
        </div>
        <div className="space-y-2">
          {filteredQueue.slice(0, 5).map((item) => {
            const op = operators.find(o => o.id === item.operatorId);
            const sector = sectors.find(s => s.id === item.sectorId);
            const isOverdue = new Date(item.slaDeadline) < new Date();
            const priorityColors: Record<string, string> = {
              high: 'var(--error)',
              medium: 'var(--warning)',
              low: 'var(--muted-foreground)',
            };
            const priorityLabels: Record<string, string> = {
              high: 'Alta',
              medium: 'Media',
              low: 'Baixa',
            };
            return (
              <div
                key={item.id}
                className="flex items-center justify-between p-3"
                style={{
                  backgroundColor: isOverdue ? 'var(--error-muted)' : 'var(--muted)',
                  borderRadius: 'var(--radius)',
                  borderLeft: `3px solid ${priorityColors[item.priority]}`,
                }}
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div>
                    <div className="flex items-center gap-2 mb-0.5">
                      <span style={{ fontWeight: 'var(--font-weight-medium)', fontSize: 'var(--text-sm)', color: 'var(--foreground)' }}>
                        {item.callId}
                      </span>
                      <span
                        className="px-1.5 py-0.5"
                        style={{
                          fontSize: 'var(--text-micro)',
                          backgroundColor: `color-mix(in srgb, ${priorityColors[item.priority]} 12%, transparent)`,
                          color: priorityColors[item.priority],
                          borderRadius: 'var(--radius)',
                          fontWeight: 'var(--font-weight-semibold)',
                        }}
                      >
                        {priorityLabels[item.priority]}
                      </span>
                      {isOverdue && (
                        <span
                          className="px-1.5 py-0.5"
                          style={{
                            fontSize: 'var(--text-micro)',
                            backgroundColor: 'var(--error)',
                            color: 'var(--error-foreground)',
                            borderRadius: 'var(--radius)',
                            fontWeight: 'var(--font-weight-semibold)',
                          }}
                        >
                          SLA Vencido
                        </span>
                      )}
                    </div>
                    <p style={{ fontSize: 'var(--text-xs)', color: 'var(--muted-foreground)' }}>
                      {op?.name || 'N/A'} — {sector?.name || 'N/A'} — {Math.floor(item.duration / 60)}m{item.duration % 60}s
                    </p>
                  </div>
                </div>
                <div className="text-right" style={{ flexShrink: 0 }}>
                  <p style={{ fontSize: 'var(--text-xs)', color: 'var(--muted-foreground)' }}>
                    SLA: {new Date(item.slaDeadline).toLocaleDateString('pt-BR')}
                  </p>
                </div>
              </div>
            );
          })}
          {filteredQueue.length === 0 && (
            <p style={{ color: 'var(--muted-foreground)', fontSize: 'var(--text-sm)', textAlign: 'center', padding: '24px 0' }}>
              Nenhuma auditoria pendente na fila.
            </p>
          )}
        </div>
      </div>

      {/* Monitores Overview (admin only) */}
      {isAdmin && effectiveMonitorId === 'all' && (
        <div className="p-5 mt-6" style={cardStyle}>
          <div className="flex items-center gap-2 mb-4">
            <Users size={20} style={{ color: 'var(--primary)' }} />
            <h3 style={{ fontWeight: 'var(--font-weight-semibold)', fontSize: 'var(--text-base)', color: 'var(--foreground)' }}>
              Visao Geral dos Monitores
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border)' }}>
                  {['Monitor', 'Tipo', 'Especializacao', `Auditorias (${new Date().toLocaleString('pt-BR', { month: 'short' }).replace('.', '')})`, 'Score Medio', 'FG Detectadas', 'Calibracao', 'Pendentes'].map(h => (
                    <th
                      key={h}
                      style={{
                        padding: '10px 12px',
                        textAlign: 'left',
                        fontSize: 'var(--text-xs)',
                        fontWeight: 'var(--font-weight-semibold)',
                        color: 'var(--muted-foreground)',
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {allMonitors.map((m) => {
                  const perf = monitorPerformance.find(p => p.monitorId === m.id && p.period === currentPeriod);
                  return (
                    <tr
                      key={m.id}
                      style={{ borderBottom: '1px solid var(--border)' }}
                    >
                      <td style={{ padding: '10px 12px', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-medium)' }}>
                        <button
                          onClick={() => setSelectedMonitorId(m.id)}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            color: 'var(--primary)',
                            fontWeight: 'var(--font-weight-medium)',
                            fontSize: 'var(--text-sm)',
                            fontFamily: 'var(--font-family-inter)',
                            textDecoration: 'underline',
                          }}
                        >
                          {m.name}
                        </button>
                      </td>
                      <td style={{ padding: '10px 12px' }}>
                        <span
                          className="flex items-center gap-1 px-2 py-0.5 w-fit"
                          style={{
                            backgroundColor: m.type === 'ai' ? 'var(--secondary)' : 'var(--muted)',
                            color: m.type === 'ai' ? 'var(--secondary-foreground)' : 'var(--foreground)',
                            fontSize: 'var(--text-xs)',
                            borderRadius: 'var(--radius)',
                          }}
                        >
                          {m.type === 'ai' ? <Bot size={12} /> : <User size={12} />}
                          {m.type === 'ai' ? 'IA' : 'Humano'}
                        </span>
                      </td>
                      <td style={{ padding: '10px 12px', fontSize: 'var(--text-xs)', color: 'var(--muted-foreground)' }}>
                        {m.specialization}
                      </td>
                      <td style={{ padding: '10px 12px', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-semibold)', fontFamily: 'var(--font-family-mono)' }}>
                        {perf?.auditsPerformed || 0}
                      </td>
                      <td style={{
                        padding: '10px 12px',
                        fontSize: 'var(--text-sm)',
                        fontWeight: 'var(--font-weight-semibold)',
                        color: (perf?.avgScoreGiven || 0) >= 70 ? 'var(--success)' : 'var(--warning)',
                        fontFamily: 'var(--font-family-mono)',
                      }}>
                        {perf?.avgScoreGiven || 0}
                      </td>
                      <td style={{
                        padding: '10px 12px',
                        fontSize: 'var(--text-sm)',
                        fontWeight: 'var(--font-weight-semibold)',
                        color: 'var(--error)',
                        fontFamily: 'var(--font-family-mono)',
                      }}>
                        {perf?.falhasGravesDetected || 0}
                      </td>
                      <td style={{
                        padding: '10px 12px',
                        fontSize: 'var(--text-sm)',
                        fontWeight: 'var(--font-weight-semibold)',
                        color: (perf?.calibrationScore || 0) >= 85 ? 'var(--success)' : 'var(--warning)',
                        fontFamily: 'var(--font-family-mono)',
                      }}>
                        {perf?.calibrationScore || 0}%
                      </td>
                      <td style={{
                        padding: '10px 12px',
                        fontSize: 'var(--text-sm)',
                        fontWeight: 'var(--font-weight-semibold)',
                        color: (perf?.pendingAudits || 0) > 5 ? 'var(--warning)' : 'var(--foreground)',
                        fontFamily: 'var(--font-family-mono)',
                      }}>
                        {perf?.pendingAudits || 0}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
